const rawStage = process.env.REACT_APP_STAGE || "dev";
const isProd = rawStage === "prod";
const STAGES = ["dev", "staging", "sandbox", "prod"] as const;
type Stage = (typeof STAGES)[number];
const isStage = (value: string): value is Stage =>
  (STAGES as readonly string[]).includes(value);
const stage: Stage = isStage(rawStage) ? rawStage : "dev";

// The main lemonPay REST API per stage. `api.lemonpayapp.com` is lemonPay-dev,
// not prod, so a deployed non-dev stage names its own host rather than falling
// back to it. Staging still targets the dev API: its own host is not wired here.
const LEMONPAY_API_URLS: Record<Stage, string> = {
  dev: "https://api.lemonpayapp.com",
  staging: "https://api.lemonpayapp.com",
  sandbox: "https://api-sandbox.lemonpayapp.com",
  prod: "https://v1.lemonpayapp.com",
};
const LEMONPAY_API_URL = LEMONPAY_API_URLS[stage];

export const SERVER = {
  BASE_URL: process.env.REACT_APP_SERVER_URL || LEMONPAY_API_URL,
};

export const ADMIN_SERVER = {
  BASE_URL:
    process.env.REACT_APP_ADMIN_SERVER_URL ||
    `https://admin-api-${stage}.lemonpayapp.com`,
};
// The klearlyMarketingService is mounted on the main app API gateway (same
// REST API as the other klearly* services), but its endpoints are protected by
// the admin Cognito user pool authorizer.
export const MARKETING_SERVER = {
  BASE_URL: process.env.REACT_APP_MARKETING_SERVER_URL || LEMONPAY_API_URL,
};

// klearlyNotificationsService's banner routes are mounted on the main app API
// gateway (same REST API as the other klearly* services), but the admin
// create/update/delete/list routes are protected by the admin Cognito pool
// authorizer.
export const BANNERS_SERVER = {
  BASE_URL: process.env.REACT_APP_BANNERS_SERVER_URL || LEMONPAY_API_URL,
};

export const API_SERVER = {
  BASE_URL:
    process.env.REACT_APP_API_SERVER_URL ||
    (isProd ? "https://apigw.klearly.eu" : `https://apigw.${stage}.klearly.eu`),
};

export const WEB_PORTAL = {
  BASE_URL:
    process.env.REACT_APP_WEB_PORTAL_URL ||
    (isProd
      ? "https://www.dashboard.klearly.eu"
      : `https://www.dashboard.${stage}.klearly.eu`),
};

export const PARTNER_HUB = {
  BASE_URL:
    process.env.REACT_APP_PARTNER_HUB_URL ||
    (isProd
      ? "https://partner.klearly.eu"
      : `https://partner.${stage}.klearly.eu`),
};

export const STAGE = stage;

export const PROD_ADMIN_SERVER_URL = "https://admin-api-prod.lemonpayapp.com";

const PROD_ADMIN_POOL_ISSUER =
  "https://cognito-idp.eu-central-1.amazonaws.com/eu-central-1_JcArOxTwU";

export type RoamEnv = "dev" | "sandbox" | "prod";

export const ROAM_TARGET_ENVS: RoamEnv[] = ["dev", "sandbox", "prod"];

export const KBM_URLS: Record<RoamEnv, string> = {
  dev: "https://dev.lemonpayapp.com",
  sandbox: "https://sandbox.lemonpayapp.com",
  prod: "https://admin.lemonpayapp.com",
};

export const getTokenIssuer = (idToken?: string | null): string | undefined => {
  if (!idToken) return undefined;
  try {
    const payload = idToken.split(".")[1];
    if (!payload) return undefined;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    const parsed = JSON.parse(window.atob(padded));
    return typeof parsed?.iss === "string" ? parsed.iss : undefined;
  } catch {
    return undefined;
  }
};

export const isProdPoolToken = (idToken?: string | null): boolean =>
  getTokenIssuer(idToken) === PROD_ADMIN_POOL_ISSUER;

export const authHostForToken = (idToken?: string | null): string =>
  isProdPoolToken(idToken) ? PROD_ADMIN_SERVER_URL : ADMIN_SERVER.BASE_URL;

export const SIZES = {
  mobileS: "320px",
  mobileM: "375px",
  mobileL: "425px",
  tablet: "768px",
  laptop: "1024px",
  laptopL: "1440px",
  desktop: "2560px",
};

export const CLIENT = {
  DEVICE: {
    mobileS: `(min-width: ${SIZES.mobileS})`,
    mobileM: `(min-width: ${SIZES.mobileM})`,
    mobileL: `(min-width: ${SIZES.mobileL})`,
    tablet: `(min-width: ${SIZES.tablet})`,
    laptop: `(min-width: ${SIZES.laptop})`,
    laptopL: `(min-width: ${SIZES.laptopL})`,
    desktop: `(min-width: ${SIZES.desktop})`,
    desktopL: `(min-width: ${SIZES.desktop})`,
  },
};

export const GOOGLE_MAPS = {
  API_KEY: "AIzaSyAdXroFT1KIhQB4ROVGUVJ7ucW767zKrEA",
};

export const CONFIG = {
  SERVER,
  ADMIN_SERVER,
  MARKETING_SERVER,
  BANNERS_SERVER,
  API_SERVER,
  CLIENT,
  GOOGLE_MAPS,
};
